import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Project } from '../models/project.model';
import { FieldExecutionService } from './field-execution.service';
import {
    aggregateFeStats,
    getProjectPhysical,
    isFeDelayed,
} from '../utils/field-execution-stats.utils';

/**
 * Live Field Execution analytics for the home page bottom summary strip.
 *
 * Subscribes only to `field_execution_projects` (via {@link FieldExecutionService})
 * — independent from {@link DashboardStatsService} / HM Decision data.
 */
@Injectable({ providedIn: 'root' })
export class FieldExecutionStatsService {
    private readonly feService = inject(FieldExecutionService);

    readonly projects = signal<Project[]>([]);
    readonly lastSync = signal<Date>(new Date());
    readonly isLive = signal(false);

    private readonly aggregated = computed(() => aggregateFeStats(this.projects()));

    readonly totalProjects = computed(() => this.aggregated().totalProjects);
    readonly sanctionedAmount = computed(() => this.aggregated().sanctionedAmount);
    readonly avgProgress = computed(() => this.aggregated().avgProgress);
    readonly delayedCount = computed(() => this.aggregated().delayedCount);

    readonly completedCount = computed(() =>
        this.projects().filter(p => p.status === 'Completed').length
    );

    readonly allProjects = computed(() => this.projects());

    readonly delayedProjects = computed<Project[]>(() =>
        this.projects().filter(p => isFeDelayed(p))
    );

    readonly completedProjects = computed<Project[]>(() =>
        this.projects().filter(p => p.status === 'Completed')
    );

    readonly progressProjects = computed<Project[]>(() =>
        [...this.projects()].sort((a, b) => getProjectPhysical(b) - getProjectPhysical(a))
    );

    constructor() {
        this.feService.getProjects$()
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: list => {
                    this.projects.set(list);
                    this.lastSync.set(new Date());
                    this.isLive.set(true);
                },
                error: err => {
                    console.warn('[FieldExecutionStatsService] Firestore stream error:', err);
                    this.isLive.set(false);
                },
            });
    }
}
